import { Template, useContent } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PageLogger, usePageLogger } from '../../bi/mfi'
import { useFinishFlowV2 } from '../../hooks/useFinishFlowV2'
import { useFlowContext } from '../../hooks/useFlowContext'
import {
  AIWelcome,
  BrandedAppCategory,
  BrandedAppSiteSelctor,
  BrandedAppWelcome,
  CardCategory,
  Category,
  CustomizeSiteEqualSplit,
  EnterpriseForm,
  EnterpriseFormThanks,
  EnterpriseFormWelcome,
  HeadlessApps,
  HeadlessBusinessName,
  HeadlessRadioInput,
  HeadlessTwoRadioInputs,
  SecondTry,
  SGLandingMobile,
  Sorry,
  Transition,
  Welcome,
  WixelSplit,
  WixIntrosBoxXL,
  WixIntrosCategory,
  WixIntrosRadiogroup,
  WixMobileApps,
  WixMobileBaseCheckbox,
  WixMobileBusinessName,
  WixMobileCategory,
  WixMobileGetOwnerApp,
  WixMobilePantone,
  WixMobileSplit,
  WixMobileWelcome,
  WixStudioAgencyDetails,
  WixStudioBulletSplit,
  WixStudioBulletSplitPantone,
  WixStudioCategory,
  WixStudioCreate,
  WixStudioInputGroup,
  WixStudioInvite,
  WixStudioInviteAccept,
  WixStudioLoader,
  WixStudioMobileSplit,
  WixStudioMobileStores,
  WixStudioMobileWelcome,
  WixStudioSimpleSplit,
  WixStudioSplit,
  WixStudioSplitV2,
  WixStudioSplitV3,
  WixStudioVelo,
  WixStudioWelcome,
  WixStudioWelcomeSwitch,
} from '../../Templates'
import { WixStudioCreateExtended } from '../../Templates/WixStudio/WixStudioCreateExtended'
import { LoadersGroup } from '../../Templates/WixStudio/WixStudioInvite/Content'
import { once } from '../../utils/once'

export const AppTemplates: FC = () => {
  const [flowContext] = useFlowContext()
  const pageLogger = usePageLogger()
  const [searchParams] = useSearchParams()
  const { templateName, content } = useContent<{ loaders: LoadersGroup }>()

  // This thing will fire once, but if user refreshes the page on welcome screen, it will be sent again and again
  const isStartFlowSent = useRef(false)
  useEffect(() => {
    if (flowContext?.historyEntries && flowContext.historyEntries.length <= 2 && !isStartFlowSent.current) {
      sendStartFlowOnce(pageLogger, flowContext.metadata.ref, searchParams.get('http_referrer') || undefined)
      isStartFlowSent.current = true
    }
  }, [pageLogger, flowContext])

  const promisesStatus = useFinishFlowV2()

  if (templateName === 'Wix Studio Invite') {
    if (promisesStatus?.creatingAccountInvites === 'pending') {
      return <WixStudioLoader title={content.loaders.content.sendingInvites} />
    }

    if (promisesStatus?.creatingAccountInvites === 'success') {
      return <WixStudioLoader title={content.loaders.content.preparingWorkspace} />
    }
  }

  return (
    <>
      {/* Lean Template */}
      <Template name="Sorry">
        <Sorry />
      </Template>
      <Template name="Customize Site Equal Split">
        <CustomizeSiteEqualSplit />
      </Template>

      {/* New Intro Concept */}
      <Template name="Welcome">
        <Welcome />
      </Template>
      <Template name="2nd Try">
        <SecondTry />
      </Template>
      <Template name="Category">
        <Category />
      </Template>
      <Template name="Card Category">
        <CardCategory />
      </Template>

      {/* Headless Flow*/}
      <Template name="Headless Apps">
        <HeadlessApps />
      </Template>
      <Template name="Headless Business Name">
        <HeadlessBusinessName />
      </Template>
      <Template name="Headless Radio Input">
        <HeadlessRadioInput />
      </Template>
      <Template name="Headless Two Radio Inputs">
        <HeadlessTwoRadioInputs />
      </Template>

      {/* Wix Studio */}
      <Template name="Wix Studio Split">
        <WixStudioSplit />
      </Template>
      <Template name="Wix Studio Split V2">
        <WixStudioSplitV2 />
      </Template>
      <Template name="Wix Studio Split V3">
        <WixStudioSplitV3 />
      </Template>
      <Template name="Wix Studio Welcome">
        <WixStudioWelcome />
      </Template>
      <Template name="Wix Studio Agency Details">
        <WixStudioAgencyDetails />
      </Template>
      <Template name="Wix Studio Invite Accept">
        <WixStudioInviteAccept />
      </Template>
      <Template name="Wix Studio Invite">
        <WixStudioInvite />
      </Template>
      <Template name="Wix Studio Create">
        <WixStudioCreate />
      </Template>
      <Template name="Wix Studio Create Extended">
        <WixStudioCreateExtended />
      </Template>
      <Template name="Wix Studio Input Group">
        <WixStudioInputGroup />
      </Template>
      <Template name="Wix Studio Bullet Split">
        <WixStudioBulletSplit />
      </Template>
      <Template name="Wix Studio Bullet Split Pantone">
        <WixStudioBulletSplitPantone />
      </Template>
      <Template name="Wix Studio Simple Split">
        <WixStudioSimpleSplit />
      </Template>
      <Template name="Wix Studio Welcome Switch">
        <WixStudioWelcomeSwitch />
      </Template>
      <Template name="Wix Studio Mobile Stores">
        <WixStudioMobileStores />
      </Template>
      <Template name="Wix Studio Mobile Welcome">
        <WixStudioMobileWelcome />
      </Template>
      <Template name="Wix Studio Mobile Split">
        <WixStudioMobileSplit />
      </Template>
      <Template name="Wix Studio Velo">
        <WixStudioVelo />
      </Template>
      <Template name="Wix Studio Category">
        <WixStudioCategory />
      </Template>

      {/* Wixel */}
      <Template name="Wixel Split">
        <WixelSplit />
      </Template>

      {/* Enterprise */}
      <Template name="Enterprise Form Welcome">
        <EnterpriseFormWelcome />
      </Template>
      <Template name="Enterprise Form Thanks">
        <EnterpriseFormThanks />
      </Template>
      <Template name="Enterprise Form">
        <EnterpriseForm />
      </Template>

      {/* Wix Intros */}
      <Template name="Wix Intros Box XL">
        <WixIntrosBoxXL />
      </Template>
      <Template name="Wix Intros Radiogroup">
        <WixIntrosRadiogroup />
      </Template>
      <Template name="Wix Intros Category">
        <WixIntrosCategory />
      </Template>

      {/* SG */}
      <Template name="SG Landing Mobile">
        <SGLandingMobile />
      </Template>

      {/* Mobile */}
      <Template name="Wix Mobile Welcome">
        <WixMobileWelcome />
      </Template>
      <Template name="Wix Mobile Category">
        <WixMobileCategory />
      </Template>
      <Template name="Wix Mobile Base Checkbox">
        <WixMobileBaseCheckbox />
      </Template>
      <Template name="Wix Mobile Apps">
        <WixMobileApps />
      </Template>
      <Template name="Wix Mobile Business Name">
        <WixMobileBusinessName />
      </Template>
      <Template name="Wix Mobile Get Owner App">
        <WixMobileGetOwnerApp />
      </Template>
      <Template name="Wix Mobile Pantone">
        <WixMobilePantone />
      </Template>
      <Template name="Wix Mobile Split">
        <WixMobileSplit />
      </Template>

      {/* Branded Apps */}
      <Template name="Branded App Site Selector">
        <BrandedAppSiteSelctor />
      </Template>
      <Template name="Branded App Category">
        <BrandedAppCategory />
      </Template>
      <Template name="Branded App Welcome">
        <BrandedAppWelcome />
      </Template>

      <Template name="Intro Transition">
        <Transition />
      </Template>

      <Template name="AI Welcome">
        <AIWelcome />
      </Template>
    </>
  )
}

const sendStartFlow = (pageLogger: PageLogger, referralInfo: string, referralUrl?: string) =>
  pageLogger.startFlow({ referralInfo, referralUrl })
const sendStartFlowOnce = once(sendStartFlow)
